import { IONote } from '../io/io-note.class';
import type { StagedSdrNote } from '../../data-services/sample/sample-delivery-request.data.service';
import { EXPENSE_STATUS_CODE } from './meeting-expense.constants';
import { MeetingExpense, applyScrap, applySubmit, isExpenseCancelled } from './meeting-expense.class';

export const PENDING_NOTE_ID_PREFIX = 'pending-note-';
export const MAX_OFFLINE_EXPENSE_ATTACHMENT_BYTES = 20 * 1024 * 1024;
const ATTACHMENT_BUDGET_ERROR = 'ExpenseAttachmentBudgetError';

export interface PendingExpenseNote {
  note: StagedSdrNote;
  clearAttachment: boolean;
}

export interface PendingExpenseChanges {
  created: boolean;
  /** A create was sent at least once, so the server may hold the record even though no response arrived. */
  createSent?: boolean;
  fieldsDirty: boolean;
  submit: boolean;
  scrap: boolean;
  notes: PendingExpenseNote[];
  noteDeletes: string[];
}

/** One Pouch doc per expense: the expense as the rep last saw it, plus what the server has not seen yet. */
export interface MeetingExpenseDoc {
  expense: MeetingExpense;
  pending?: PendingExpenseChanges;
}

export type ExpenseUploadStep =
  | { kind: 'create' }
  | { kind: 'fields' }
  | { kind: 'note'; entry: PendingExpenseNote }
  | { kind: 'noteDelete'; annotationId: string }
  | { kind: 'submit' }
  | { kind: 'scrap' };

export type ExpenseUploadPlan =
  | { kind: 'steps'; steps: ExpenseUploadStep[] }
  | { kind: 'alreadyApplied' }
  | { kind: 'serverWins' }
  | { kind: 'gone' };

export const meetingKey = (id: string | null | undefined): string => (id ?? '').toLowerCase();

export const isOfflineMeetingId = (id: string | null | undefined): boolean => !!id && id.startsWith('offline_');

export const isDraftStatusCode = (statusCode: number): boolean =>
  statusCode === EXPENSE_STATUS_CODE.DRAFT || statusCode === EXPENSE_STATUS_CODE.ACTIVE;

export const isExpenseAttachmentBudgetError = (err: unknown): boolean =>
  (err as Error | undefined)?.name === ATTACHMENT_BUDGET_ERROR;

const emptyPending = (): PendingExpenseChanges => ({
  created: false, fieldsDirty: false, submit: false, scrap: false, notes: [], noteDeletes: [],
});

const isPendingEmpty = (pending: PendingExpenseChanges): boolean =>
  !pending.created && !pending.fieldsDirty && !pending.submit && !pending.scrap && !pending.notes.length && !pending.noteDeletes.length;

const noteKey = (note: StagedSdrNote): string | undefined => note.existingAnnotationId ?? note.clientId;

function withPending(doc: MeetingExpenseDoc, change: Partial<PendingExpenseChanges>, expense = doc.expense): MeetingExpenseDoc {
  const pending = { ...(doc.pending ?? emptyPending()), ...change };
  return isPendingEmpty(pending) ? { expense } : { expense, pending };
}

export function stageCreate(expense: MeetingExpense, createSent = false): MeetingExpenseDoc {
  return { expense, pending: { ...emptyPending(), created: true, createSent } };
}

export const markCreateSent = (doc: MeetingExpenseDoc): MeetingExpenseDoc => withPending(doc, { createSent: true });

// The server already holds the record; edits staged since the create still go up as a field update.
export const markCreated = (doc: MeetingExpenseDoc): MeetingExpenseDoc => withPending(doc, { created: false, createSent: false });

// The stored status and server notes win: an edit must never undo a staged submit or re-add a deleted note.
export function stageFieldEdit(doc: MeetingExpenseDoc | null, expense: MeetingExpense): MeetingExpenseDoc {
  const base = doc ?? { expense };
  return withPending(base, { fieldsDirty: true }, { ...expense, statusCode: base.expense.statusCode, statusLabel: base.expense.statusLabel, notes: base.expense.notes });
}

export function stageSubmit(doc: MeetingExpenseDoc): MeetingExpenseDoc {
  return withPending(doc, { submit: true }, applySubmit(doc.expense));
}

/** Null when the expense never reached the server: it is dropped, never sent. */
export function stageScrap(doc: MeetingExpenseDoc): MeetingExpenseDoc | null {
  if (doc.pending?.created && !doc.pending.createSent) return null;
  return withPending(doc, { scrap: true, fieldsDirty: false, submit: false, notes: [], noteDeletes: [] }, applyScrap(doc.expense));
}

export function stageNoteUpsert(doc: MeetingExpenseDoc, note: StagedSdrNote, clearAttachment: boolean): MeetingExpenseDoc {
  const key = noteKey(note);
  const notes = [...(doc.pending?.notes ?? [])];
  const index = notes.findIndex((entry) => noteKey(entry.note) === key);
  const previous = index >= 0 ? notes[index] : undefined;
  // A reseeded note carries no file body; keep the staged file unless the rep removed it.
  const attachment = note.attachment?.documentBody || clearAttachment ? note.attachment : previous?.note.attachment;
  const entry: PendingExpenseNote = {
    note: { ...note, attachment },
    clearAttachment: !attachment && (clearAttachment || !!previous?.clearAttachment),
  };
  if (index >= 0) notes[index] = entry;
  else notes.push(entry);
  const stagedBytes = notes.reduce((sum, staged) => sum + (staged.note.attachment?.fileSize ?? 0), 0);
  if (stagedBytes > MAX_OFFLINE_EXPENSE_ATTACHMENT_BYTES) {
    const error = new Error('Offline attachments for this expense exceed the budget');
    error.name = ATTACHMENT_BUDGET_ERROR;
    throw error;
  }
  return withPending(doc, { notes });
}

export function stageNoteRemove(doc: MeetingExpenseDoc, note: StagedSdrNote): MeetingExpenseDoc {
  const key = noteKey(note);
  const notes = (doc.pending?.notes ?? []).filter((entry) => noteKey(entry.note) !== key);
  const noteDeletes = doc.pending?.noteDeletes ?? [];
  return withPending(doc, {
    notes,
    noteDeletes: note.existingAnnotationId ? [...noteDeletes, note.existingAnnotationId] : noteDeletes,
  });
}

function editedNote(note: IONote, entry: PendingExpenseNote): IONote {
  const file = entry.note.attachment;
  const keepFile = !file && !entry.clearAttachment;
  return new IONote({
    annotationid: note.noteId,
    notetext: entry.note.noteText,
    ownerName: note.ownerName,
    createdon: note.createdTime,
    isdocument: !!file || (keepFile && note.hasDocument),
    filename: file?.fileName ?? (keepFile ? note.documentName : undefined),
    filesize: file?.fileSize ?? (keepFile ? note.documentSize : undefined),
    mimetype: file?.mimeType ?? (keepFile ? note.documentMimeType : undefined),
  });
}

function stagedNote(note: StagedSdrNote, ownerName: string): IONote {
  return new IONote({
    annotationid: `${PENDING_NOTE_ID_PREFIX}${note.clientId}`,
    notetext: note.noteText,
    ownerName,
    createdon: new Date(),
    isdocument: !!note.attachment,
    filename: note.attachment?.fileName,
    filesize: note.attachment?.fileSize,
    mimetype: note.attachment?.mimeType,
  });
}

export type OnlineNoteChange =
  | { kind: 'upsert'; annotationId: string; note: StagedSdrNote; clearAttachment: boolean }
  | { kind: 'remove'; annotationId: string };

/** Mirrors a note write that already reached Dynamics into the cached server notes. */
export function applyOnlineNoteChange(doc: MeetingExpenseDoc, change: OnlineNoteChange): MeetingExpenseDoc {
  const notes = doc.expense.notes ?? [];
  if (change.kind === 'remove') {
    return { ...doc, expense: { ...doc.expense, notes: notes.filter((note) => note.noteId !== change.annotationId) } };
  }
  const entry: PendingExpenseNote = { note: change.note, clearAttachment: change.clearAttachment };
  const existing = notes.find((note) => note.noteId === change.annotationId);
  const base = existing ?? new IONote({ annotationid: change.annotationId, ownerName: doc.expense.ownerName, createdon: new Date() });
  const edited = editedNote(base, entry);
  const next = existing ? notes.map((note) => (note === existing ? edited : note)) : [...notes, edited];
  return { ...doc, expense: { ...doc.expense, notes: next } };
}

/** The expense as the UI should show it: stored server notes with staged edits applied, then staged new notes. */
export function expenseFromDoc(doc: MeetingExpenseDoc): MeetingExpense {
  const server = (doc.expense.notes ?? []).map((note) => new IONote(note as any));
  const pending = doc.pending;
  if (!pending) return { ...doc.expense, notes: server, pendingSync: false };
  const deleted = new Set(pending.noteDeletes);
  const edits = new Map(pending.notes.filter((entry) => entry.note.existingAnnotationId).map((entry) => [entry.note.existingAnnotationId, entry]));
  const kept = server
    .filter((note) => !deleted.has(note.noteId))
    .map((note) => {
      const edit = edits.get(note.noteId);
      return edit ? editedNote(note, edit) : note;
    });
  const added = pending.notes
    .filter((entry) => !entry.note.existingAnnotationId)
    .map((entry) => stagedNote(entry.note, doc.expense.ownerName));
  return { ...doc.expense, notes: [...kept, ...added], pendingSync: true };
}

/** What goes into Pouch: server notes only (staged ones live in `pending`), and never a file body. */
export function toStoredExpense(expense: MeetingExpense): MeetingExpense {
  const notes = (expense.notes ?? [])
    .filter((note) => !note.noteId?.startsWith(PENDING_NOTE_ID_PREFIX))
    .map((note) => ({ ...note, documentDataURL: '' }) as IONote);
  return { ...expense, notes, pendingSync: undefined };
}

/** `serverStatus` is undefined when the server has no record with this id. */
export function planUpload(pending: PendingExpenseChanges, serverStatus: number | undefined): ExpenseUploadPlan {
  const onServer = serverStatus !== undefined;
  // Nothing left to send: the rep's scrap already matches the server.
  if (pending.scrap && (!onServer || serverStatus === EXPENSE_STATUS_CODE.CANCELLED)) return { kind: 'steps', steps: [] };
  // An online submit that timed out after committing was queued; its fields went with it.
  if (!pending.scrap && pending.submit && serverStatus === EXPENSE_STATUS_CODE.SUBMITTED) return { kind: 'alreadyApplied' };
  if (onServer && !isDraftStatusCode(serverStatus)) return { kind: 'serverWins' };
  if (!onServer && !pending.created) return { kind: 'gone' };
  if (pending.scrap) return { kind: 'steps', steps: [{ kind: 'scrap' }] };
  const steps: ExpenseUploadStep[] = [];
  if (!onServer) steps.push({ kind: 'create' });
  else if (pending.fieldsDirty) steps.push({ kind: 'fields' });
  for (const entry of pending.notes) steps.push({ kind: 'note', entry });
  for (const annotationId of pending.noteDeletes) steps.push({ kind: 'noteDelete', annotationId });
  if (pending.submit) steps.push({ kind: 'submit' });
  return { kind: 'steps', steps };
}

/** The doc after `step` reached the server; null once the expense is gone from the device. */
export function completeStep(doc: MeetingExpenseDoc, step: ExpenseUploadStep): MeetingExpenseDoc | null {
  const pending = doc.pending ?? emptyPending();
  switch (step.kind) {
    case 'create':
      return withPending(doc, { created: false, createSent: false, fieldsDirty: false });
    case 'fields':
      return withPending(doc, { fieldsDirty: false });
    case 'note': {
      const key = noteKey(step.entry.note);
      return withPending(doc, { notes: pending.notes.filter((entry) => noteKey(entry.note) !== key) });
    }
    case 'noteDelete': {
      const expense = { ...doc.expense, notes: doc.expense.notes.filter((note) => note.noteId !== step.annotationId) };
      return withPending(doc, { noteDeletes: pending.noteDeletes.filter((id) => id !== step.annotationId) }, expense);
    }
    case 'submit':
      return withPending(doc, { submit: false });
    case 'scrap':
      return null;
  }
}

export function idsMissingFromDevice(liveIds: Set<string>, cached: MeetingExpenseDoc[], synced: MeetingExpense[]): string[] {
  const known = new Set([...cached.map((doc) => doc.expense.id), ...synced.map((expense) => expense.id)]);
  return [...liveIds].filter((id) => !known.has(id));
}

export function mergeSyncedExpenses(
  existing: MeetingExpenseDoc[],
  synced: MeetingExpense[],
  liveIds: Set<string>,
): { put: MeetingExpenseDoc[]; remove: string[] } {
  const existingIds = new Set(existing.map((doc) => doc.expense.id));
  const pendingIds = new Set(existing.filter((doc) => doc.pending).map((doc) => doc.expense.id));
  const put: MeetingExpenseDoc[] = [];
  const remove = new Set<string>();
  for (const expense of synced) {
    if (pendingIds.has(expense.id)) continue;
    if (!isExpenseCancelled(expense)) put.push({ expense: toStoredExpense(expense) });
    else if (existingIds.has(expense.id)) remove.add(expense.id);
  }
  // The id query runs alongside the delta, so a row the delta returned live stays even if the ids missed it.
  const putIds = new Set(put.map((doc) => doc.expense.id));
  for (const doc of existing) {
    const id = doc.expense.id;
    if (!doc.pending && !liveIds.has(id) && !putIds.has(id)) remove.add(id);
  }
  return { put, remove: [...remove] };
}
